import { useCallback, useEffect, useRef } from 'react';
import { useStore } from 'jotai';

import { calcTargetKeyAtom } from 'atoms/Ltool/calcTargetKeyAtom';

export type SubmitTargetGuard = {
    // 送信時の対象 key（送信中の管理など、対象単位の状態のキーに使う）
    key: string;
    // 応答時点で、送信時の対象の応答として画面に出してはいけないか
    isStale: () => boolean;
    // この画面がまだ表示中か
    isMounted: () => boolean;
};

// L の計算画面で、送信（await）後の応答を画面に出してよいかを判定する（issue #56）。
// L 専用: 対象 key（calcTargetKeyAtom）も経路（/l-tools）も L に固定している。R の handler に流用すると
// 誤った判定になるため、使う場合は対象 key と経路を引数化して一般化すること。
//
// 判定は mount 状態に依存しない。計算セットの切替（ヘッダの再選択）は stageConfigAtom を更新して
// 別 Route へ遷移し、この画面ごと unmount されるため、mount 中にしか更新されない ref では検出できない。
// そこで応答時に「今の対象」を store から読み直して送信時の key と比べる。
//
// 画面を離れただけで対象が変わっていない場合は、L のままなら失敗を黙殺しない。
// ただし L ツール自体を離れた（R 画面など）後は、前の送信の応答を出さない。
// mount 中は常に L の計算画面にいるので、経路は unmount 後にだけ見る（BrowserRouter・basename なし）。
export const useSubmitTargetGuard = () => {
    const store = useStore();
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // 送信の直前に呼ぶ。
    return useCallback((): SubmitTargetGuard => {
        const key = store.get(calcTargetKeyAtom);
        return {
            key,
            isStale: () =>
                store.get(calcTargetKeyAtom) !== key ||
                (!mountedRef.current && !window.location.pathname.startsWith('/l-tools')),
            isMounted: () => mountedRef.current,
        };
    }, [store]);
};
